import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { useDataProvider } from "src/mtska-frontend-data-provider";
import { Box, CircularProgress } from "@mui/joy";
import Tooltip from '@mui/material/Tooltip';
import { PickersDay } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { MultiBookingFormDialog } from "../Dialog";
import { itIT, enUS } from '@mui/x-date-pickers/locales';
import { AppContext } from "src/mtska-frontend-app-scaffolding";

const WidgetMultiCalendarBooking = (params) => {
    const { t, i18n } = useTranslation();

    const [dateRange, setDateRange] = useState([null, null]);
    const [hasSelectionCompleted, setHasSelectionCompleted] = useState(false);
    const [drawable, setDrawable] = useState(true);
    const [warningMessage, setWarningMessage] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [possibleDrivers, setPossibleDrivers] = useState({});

    const {innerWidth} = useContext(AppContext);
    
    const handleOpenBookingDialog = useCallback(() => {
        setDialogOpen(true);
    }, []);

    const handleSavedCalendar = useCallback(() => {
        setDialogOpen(false);
    }, []);

    useEffect(() => {
        setPossibleDrivers(params.currentDriver);
    }, [params.currentDriver]);

    const onChange = (newValue) => {
        setWarningMessage(null);
        setHasError(false);

        if (hasSelectionCompleted && newValue[0]) {
            setDateRange([newValue[0], null]);
            setHasSelectionCompleted(false);
            return;
        }

        if (newValue[0] && newValue[1]) {
            
            setDateRange([newValue[0], newValue[1]]);
            setHasSelectionCompleted(true);

            if (newValue[0] && newValue[1] && !warningMessage) {
                setDialogOpen(true);
            }
        } else {
            // Caso selezione parziale (primo click)
            setDateRange(newValue);
        }
    }


    return (
        !drawable ? <Box className="form-element form-element-calendar-booking-widget col-12"><CircularProgress /></Box> :
            <Box className="form-element form-element-calendar-booking-widget col-12">
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.resolvedLanguage === 'it' ? itIT : enUS}
                >
                    <StaticDateRangePicker
                        value={dateRange}
                        toolbar={null}
                        showToolbar={false}
                        onChange={onChange}
                        slots={{ day: CustomPickersDay }}
                        localeText={i18n.resolvedLanguage === 'it'
                            ? itIT.components.MuiLocalizationProvider.defaultProps.localeText
                            : enUS.components.MuiLocalizationProvider.defaultProps.localeText
                        }
                        slotProps={{
                            day: (ownerState) => {
                                return {
                                    selectedDays: ownerState.selectedDays || [],
                                    currentDriver: params.currentDriver[0] ?? null,
                                    hasError: hasError,
                                    onOpenBookingDialog: handleOpenBookingDialog
                                }
                            },
                            actionBar: {
                                sx: { display: 'none' }
                            }
                        }}
                        disablePast
                        calendars={innerWidth < 600 ? 1 : 4}
                        sx={{
                            "& .MuiDateRangeCalendar-root": {
                                justifyContent: "center",
                            },

                            "& .MuiPickersSlideTransition-root": {
                                minHeight: "210px",
                            },

                            "& .MuiPickersDay-root": {
                                margin: "0px",
                            },

                        }}
                    />
                    <Box>{warningMessage}</Box>
                </LocalizationProvider>

                <MultiBookingFormDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    //onSave={handleSavedCalendar}
                    onComplete={params.onComplete}
                    dateRange={dateRange}
                    warningMessage={warningMessage}
                    possibleDrivers={possibleDrivers}
                />
            </Box>

    );
};

const CustomPickersDay = React.memo((props) => {
    const { t } = useTranslation();
    const {
        day,
        outsideCurrentMonth,
        selected,
        selectedDays = [],
        optionedDates = new Set(),
        recoveryDates = new Set(),
        bookingsData = {},
        disabled,
        hasError,
        currentDriver,
        onOpenBookingDialog,
        requiredStartDay = null,
        ...other
    } = props;

    const dateStr = useMemo(() => day.format("YYYY-MM-DD"), [day]);
    const bookingInfo = useMemo(() => bookingsData[dateStr], [bookingsData, dateStr]);
    const isOptioned = optionedDates.has(dateStr);
    const isRecovery = recoveryDates.has(dateStr);
    const [start, end] = useMemo(() => selectedDays.map(d => dayjs(d)), [selectedDays]);
    const isCompleteSelection = start && end;

    // Calcolo stati
    const isRangeStart = useMemo(() => selected && start?.isSame(day, 'day'), [selected, start, day]);
    const isRangeEnd = useMemo(() => selected && end?.isSame(day, 'day'), [selected, end, day]);
    const isInRange = useMemo(() => start && end && day.isAfter(start, 'day') && day.isBefore(end, 'day'), [start, end, day]);

    const canStartOnThisDay = useMemo(() => {
        if (isOptioned || isRecovery || requiredStartDay === null || isNaN(requiredStartDay) || (selectedDays[0] !== null && selectedDays[1] === null && !isInRange)) return true;
        if (disabled) return false;

        return day.day() === requiredStartDay;
    }, [day, selectedDays, requiredStartDay, isInRange]);

    const styles = useMemo(() => ({
        base: {
            borderRadius: 0,
            border: '1px solid transparent',
            '&&.Mui-selected': {},
            '&.Mui-disabled': {
                opacity: 1
            }
        },
        occupied: {
            color: 'white',
            backgroundColor: 'rgba(245, 124, 0, 0.8)',
            borderColor: 'rgba(245, 124, 0, 0.42)',
            pointerEvents: 'none'
        },
        recovering: {
            backgroundColor: 'rgba(245, 124, 0, 0.12)',
            borderColor: 'rgba(245, 124, 0, 0.12)',
            pointerEvents: 'none'
        },
        occupiedAnonymous: {
            color: 'white',
            backgroundColor: 'rgba(255, 216, 176, 0.8)',
            borderColor: 'rgba(255, 216, 176, 0.42)',
            pointerEvents: 'none'
        },
        recoveringAnonymous: {
            backgroundColor: 'rgba(255, 216, 176, 0.12)',
            borderColor: 'rgba(255, 216, 176, 0.12)',
            pointerEvents: 'none'
        },
        startForbidden: {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            color: 'rgba(0, 0, 0, 0.20)',
            pointerEvents: 'none'
        },
        errorRange: {
            backgroundColor: 'rgba(244, 67, 54, 0.12)',
            borderColor: 'rgba(244, 67, 54, 0.12)'
        },
        errorStartEnd: {
            backgroundColor: 'rgba(244, 67, 54, 1)',
            color: 'white'
        },
        normalRange: {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            borderColor: 'rgba(25, 118, 210, 0.12)'
        },
        normalStartEnd: {
            backgroundColor: 'rgba(25, 118, 210, 1)',
            color: 'white'
        }
    }), []);

    const getStyle = useCallback((conditions) => {
        const { hasError, isInRange, isRangeStart, isRangeEnd, isOccupied, isRecovering, canStartOnThisDay, currentDriver, bookingInfo } = conditions;
        const style = { ...styles.base };

        //console.info('getStyle', currentDriver?.employment?.person?.email, bookingInfo?.email)

        if (isOccupied) {
            if (currentDriver && currentDriver?.employment?.person?.email != bookingInfo?.email) {
                Object.assign(style, styles.occupiedAnonymous);
            } else {
                Object.assign(style, styles.occupied);
            }
        } else if (isRecovering) {

            if (currentDriver && currentDriver?.employment?.person?.email != bookingInfo?.email) {
                Object.assign(style, styles.recoveringAnonymous);
            } else {
                Object.assign(style, styles.recovering);
            }

        } else if (isRangeStart || isRangeEnd) {
            Object.assign(style, hasError ? styles.errorStartEnd : styles.normalStartEnd, {
                '&&.Mui-selected': {
                    backgroundColor: hasError ? styles.errorStartEnd.backgroundColor : styles.normalStartEnd.backgroundColor,
                    color: 'white',
                    borderTopLeftRadius: isRangeStart ? '50%' : 0,
                    borderBottomLeftRadius: isRangeStart ? '50%' : 0,
                    borderTopRightRadius: isRangeEnd ? '50%' : 0,
                    borderBottomRightRadius: isRangeEnd ? '50%' : 0
                }
            });
        } else if (isInRange) {
            Object.assign(style, hasError ? styles.errorRange : styles.normalRange);
        } else if (!canStartOnThisDay) {
            Object.assign(style, styles.startForbidden);
        }

        return style;
    }, [styles]);

    const tooltipContent = useMemo(() => {
        if (isOptioned && bookingInfo && currentDriver && currentDriver?.employment?.person?.email != bookingInfo?.email) {
            return (
                <Box>
                    <Box>
                        {t('Booked at: {{date}}', { date: dayjs(bookingInfo.created_at).format("DD/MM/YYYY") })}
                    </Box>
                </Box>
            );
        } else if (isOptioned && bookingInfo) {
            return (
                <Box>
                    <Box fontWeight="bold">
                        {bookingInfo.firstname} {bookingInfo.lastname}
                    </Box>
                    <Box fontSize="0.7rem" mt={0.5}>
                        {t('Booked at: {{date}}', { date: dayjs(bookingInfo.created_at).format("DD/MM/YYYY") })}
                    </Box>
                    {bookingInfo.notes && (
                        <Box fontSize="0.7rem" mt={1} fontStyle="italic">
                            {bookingInfo.notes}
                        </Box>
                    )}
                </Box>
            );
        }
        return null;
    }, [isOptioned, bookingInfo, t]);

    const handleDayClick = useCallback(() => {
        if (isOptioned || isRecovery || !canStartOnThisDay) return;

        if (isCompleteSelection && selected && !disabled) {
            onOpenBookingDialog();
        }
    }, [isCompleteSelection, selected, disabled, onOpenBookingDialog]);

    return (
        <Tooltip
            title={tooltipContent}
            arrow
            placement="top"
            enterDelay={300}
            leaveDelay={200}
            disableInteractive
            PopperProps={{
                sx: {
                    '& .MuiTooltip-tooltip': {
                        bgcolor: 'rgba(97, 97, 97, 0.9)',
                        fontSize: '0.8rem',
                        maxWidth: '200px'
                    }
                }
            }}
        >
            <span>
                <PickersDay
                    {...other}
                    day={day}
                    outsideCurrentMonth={outsideCurrentMonth}
                    selected={selected}
                    disabled={disabled}
                    onClick={handleDayClick}
                    sx={{
                        ...getStyle({
                            hasError,
                            isInRange,
                            isRangeStart,
                            isRangeEnd,
                            isOccupied: isOptioned && !disabled,
                            isRecovering: isRecovery && !disabled,
                            currentDriver: currentDriver,
                            bookingInfo: bookingInfo,
                            canStartOnThisDay: canStartOnThisDay
                        }),
                        ...(isCompleteSelection && !disabled && {
                            cursor: 'pointer',
                            '&:hover': {
                                transform: 'scale(1.05)'
                            }
                        }),
                    }}
                />
            </span>
        </Tooltip>
    );
});

export default WidgetMultiCalendarBooking;